import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../context/LanguageContext';

const Card = styled.div`
  background-color: ${props => props.theme.colors.card};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius['2xl']};
  padding: 1.25rem;
  
  @media (min-width: 1024px) {
    padding: 1.5rem;
  }
  
  @media (min-width: 1440px) {
    padding: 1.75rem;
  }
`;

const Title = styled.h3`
  font-family: ${props => props.theme.typography.fonts.display};
  font-size: 1.25rem;
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  margin-bottom: 1rem;
  
  @media (min-width: 1024px) {
    font-size: 1.375rem;
    margin-bottom: 1.25rem;
  }
  
  @media (min-width: 1440px) {
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
  }
`;

const ArticlesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  
  @media (min-width: 1024px) {
    gap: 1.25rem;
  }
  
  @media (min-width: 1440px) {
    gap: 1.5rem;
  }
`;

const ArticleLink = styled(Link)`
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  text-decoration: none;
  color: inherit;
  
  @media (min-width: 1024px) {
    gap: 1rem;
  }
  
  @media (min-width: 1440px) {
    gap: 1.25rem;
  }
`;

const Thumbnail = styled.div`
  position: relative;
  width: 4rem;
  height: 4rem;
  border-radius: ${props => props.theme.borderRadius.xl};
  overflow: hidden;
  flex-shrink: 0;
  
  @media (min-width: 1024px) {
    width: 5rem;
    height: 5rem;
  }
  
  @media (min-width: 1440px) {
    width: 6rem;
    height: 6rem;
  }
`;

const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s;
  
  ${ArticleLink}:hover & {
    transform: scale(1.1);
  }
`;

const ArticleContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const CategoryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
`;

const ColorDot = styled.span`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 9999px;
  background-color: ${props => {
    const color = props.color;
    return props.theme.colors.category[color] || props.theme.colors.primary;
  }};
  
  @media (min-width: 1024px) {
    width: 0.625rem;
    height: 0.625rem;
  }
`;

const CategoryName = styled.span`
  font-size: 0.75rem;
  color: ${props => props.theme.colors.mutedForeground};
  
  @media (min-width: 1024px) {
    font-size: 0.8125rem;
  }
  
  @media (min-width: 1440px) {
    font-size: 0.875rem;
  }
`;

const ArticleTitle = styled.h4`
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.4;
  margin-bottom: 0.25rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  transition: color 0.2s;
  
  @media (min-width: 1024px) {
    font-size: 0.9375rem;
    margin-bottom: 0.375rem;
  }
  
  @media (min-width: 1440px) {
    font-size: 1rem;
    margin-bottom: 0.5rem;
  }
  
  ${ArticleLink}:hover & {
    color: ${props => props.theme.colors.mutedForeground};
  }
`;

const ReadTime = styled.span`
  font-size: 0.75rem;
  color: ${props => props.theme.colors.mutedForeground};
  margin-top: 0.25rem;
  
  @media (min-width: 1024px) {
    font-size: 0.8125rem;
  }
  
  @media (min-width: 1440px) {
    font-size: 0.875rem;
  }
`;

const ViewAllLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid ${props => props.theme.colors.border};
  font-size: 0.875rem;
  font-weight: 500;
  color: ${props => props.theme.colors.mutedForeground};
  text-decoration: none;
  transition: color 0.2s;
  
  @media (min-width: 1024px) {
    margin-top: 1.25rem;
    padding-top: 1.25rem;
    font-size: 0.9375rem;
  }
  
  @media (min-width: 1440px) {
    margin-top: 1.5rem;
    padding-top: 1.5rem;
    font-size: 1rem;
  }
  
  &:hover {
    color: ${props => props.theme.colors.foreground};
  }
  
  svg {
    width: 1rem;
    height: 1rem;
    
    @media (min-width: 1024px) {
      width: 1.125rem;
      height: 1.125rem;
    }
    
    @media (min-width: 1440px) {
      width: 1.25rem;
      height: 1.25rem;
    }
  }
`;

const SimilarArticles = ({ articles, title }) => {
  const { t } = useTranslation();
  const { getArticleUrl, region } = useLanguage();
  const heading = title || t('article.similarArticles', 'Similar Articles');
  const homePath = region?.code && String(region.code).toUpperCase() !== 'US'
    ? `/${String(region.code).toLowerCase()}`
    : '/';

  if (!articles?.length) return null;

  return (
    <Card>
      <Title>{heading}</Title>
      
      <ArticlesList>
        {articles.map((article) => (
          <ArticleLink key={article._id || article.slug} to={getArticleUrl(article.slug)}>
            <Thumbnail>
              <ThumbnailImage src={article.imageUrl} alt={article.imageAlt || article.title} />
            </Thumbnail>
            
            <ArticleContent>
              <CategoryRow>
                <ColorDot color={article.categoryColor} />
                <CategoryName>{article.category}</CategoryName>
              </CategoryRow>
              <ArticleTitle>{article.title}</ArticleTitle>
              <ReadTime>{article.readTime}</ReadTime>
            </ArticleContent>
          </ArticleLink>
        ))}
      </ArticlesList>

      <ViewAllLink to={homePath}>
        {t('article.viewAllArticles', 'View All Articles')}
        <ArrowRight />
      </ViewAllLink>
    </Card>
  );
};

export default SimilarArticles;

